import type { Strings } from "./i18n.js";

/**
 * The live meter's judgement (FR-2.5).
 *
 * The one rule that is easy to get wrong: deviation is measured **against the
 * boundary actually crossed**, never against the midpoint. "+2 % über Limit"
 * means two percent past the stated maximum -- not two percent past the middle
 * of the band. Reading it the obvious way overstates how far off the athlete is.
 */

export type VerdictTone = "neutral" | "success" | "warning" | "info";

export interface Verdict {
  tone: VerdictTone;
  label: string;
  /** Bar fill, 0-100, capped. */
  fillPct: number;
  fillColor: string;
  /** Where the lower edge of the target band sits, 0-100. Null when irrelevant. */
  markerPct: number | null;
  /** The headline number and its unit. */
  value: number;
  unit: "g/h" | "g";
  /** Comparison line, e.g. "von 75–98". Empty for swim. */
  comparison: string;
}

export interface VerdictInput {
  perHourAvg: number;
  totalCarbs: number;
  ceilingMin: number;
  ceilingMax: number;
  isSwim: boolean;
  isEmpty: boolean;
  t: Strings;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Percentages below 1 are shown as `+<1` rather than rounded to 0. */
function formatDeviation(pct: number): string {
  if (Math.abs(pct) < 1 && pct !== 0) return pct > 0 ? "+<1" : "<1";
  return pct > 0 ? `+${Math.round(pct)}` : String(Math.round(pct));
}

export function verdictOf(input: VerdictInput): Verdict {
  const { perHourAvg, totalCarbs, ceilingMin, ceilingMax, isSwim, isEmpty, t } = input;

  // What the headline number *is* depends on the leg: a ~30-minute swim with one
  // pre-start gel has no meaningful hourly rate, so it reports the selected
  // total instead of a rate against a ceiling.
  const figures = isSwim
    ? { value: round1(totalCarbs), unit: "g" as const, comparison: t.carbsSelected, markerPct: null }
    : {
        value: round1(perHourAvg),
        unit: "g/h" as const,
        comparison: `${t.liveOf} ${Math.round(ceilingMin)}–${Math.round(ceilingMax)}`,
        markerPct: (ceilingMin / ceilingMax) * 100,
      };

  // Emptiness outranks everything, including the swim case: "no limit applies"
  // would be a claim about a selection that does not exist yet.
  if (isEmpty) {
    return {
      ...figures,
      tone: "neutral",
      label: t.liveNothing,
      fillPct: 0,
      fillColor: "var(--ink-200)",
    };
  }

  if (isSwim) {
    return {
      ...figures,
      tone: "info",
      label: t.swimNoLimit,
      fillPct: 100,
      fillColor: "var(--fuel-fluid)",
    };
  }

  const base = { ...figures, fillPct: Math.min(100, (perHourAvg / ceilingMax) * 100) };
  const over = perHourAvg > ceilingMax;
  const under = perHourAvg < ceilingMin;

  if (!over && !under) {
    return { ...base, tone: "success", label: t.onTarget, fillColor: "var(--success)" };
  }

  const boundary = over ? ceilingMax : ceilingMin;
  const pct = (perHourAvg / boundary) * 100 - 100;

  return {
    ...base,
    tone: over ? "warning" : "info",
    label: `${formatDeviation(pct)}% ${over ? t.over : t.under}`,
    fillColor: over ? "var(--warning)" : "var(--fuel-fluid)",
  };
}
